(ns ncr.etc.rhf
  (:require
   [clojure.string :as str]
   [clojure.walk :as walk]
   [taoensso.encore :as e]
   ["react-hook-form" :refer [useForm Controller]]
   [uix.core.alpha :as uix]
   [camel-snake-kebab.core :as csk]
   [ncr.widgets.core :as w]
   [ncr.etc.pf :as pf]
   [ncr.etc.modal :as m]
   [ncr.etc.util :as u]))

(def cc-regexp
  (js/RegExp. "-(\\w)" "g"))

(defn- cc-fn [s]
  (str/upper-case (aget s 1)))

(defn cc-map [m]
  (e/map-keys
    (fn [k]
      (cond
        (keyword? k)
        (keyword (.replace (name k) cc-regexp cc-fn))

        (string? k)
        (.replace k cc-regexp cc-fn)

        :else
        (e/have! map? k))) m))

(defn dash-to-camel [name-str]
  (.replace name-str cc-regexp cc-fn))

(defn obj->map [obj]
  (->
    (fn [acc key]
      (let [v (goog.object/get obj key)]
        (assoc acc (keyword key) v)))
    (reduce {} (.getKeys ^js goog/object obj))))

(defn ->regopt [options]
  (letfn
    [(->val [v]
       (clj->js v))]

    (clj->js
      (merge
        (when-let [ref (:ref options)]
          {:ref ref})
        {:required (-> options :required clj->js)
         :maxLength (-> options :max-length clj->js)
         :minLength (-> options :min-length clj->js)
         :max (-> options :max clj->js)
         :min (-> options :min clj->js)
         :pattern (-> options :pattern clj->js)
         :validate (:validate options)
         :valueAsNumber (:value-as-number options)
         :valueAsDate (:value-as-date options)
         :setValueAs (:set-value-as options)
         :shouldUnregister (:should-unregister options)}))))

(declare js->clj* clj->js*)

(defn js->clj* [in]
  (walk/prewalk
    (fn [e]
      (cond
        (map? e)
        (e/map-keys (comp keyword csk/->kebab-case) e)

        (fn? e)
        (fn [& args]
          (let [args (map clj->js* args)]
            (js->clj* (apply e args))))

        :else
        e))
    (js->clj in)))

(defn clj->js* [in]
  (clj->js
    (walk/prewalk
      (fn [e]
        (cond
          (map? e)
          (e/map-keys csk/->camelCase e)

          (fn? e)
          (fn [& args]
            (let [args (map js->clj* args)]
              (clj->js* (apply e args))))

          :else
          e))
      in)))

(defn use-form' [args]
  (let
    [{:keys [register] :as form}
     (js->clj*
       (useForm (clj->js* args)))]
    (merge form
      {:register-pf
       (fn register-pf [& args]
         (let [register (apply register args)]
           (update register :on-change
             (fn [original]
               (fn [_ event]
                 (original event))))))})))

(defn use-form
  ([]
   (use-form {}))
  ([options]

   (let
     [^js form (useForm (clj->js (cc-map options)))]
     (merge
       (letfn
         [(make-registration [ref options]
            (let [^js registration ((.-register form) (clj->js ref) (->regopt options))]
              {:on-change (.-onChange registration)
               :on-blur (.-onBlur registration)
               :ref (.-ref registration)
               :name (.-name registration)}))]

         {:register
          (fn register
            ([ref]
             (register ref {}))
            ([ref options]
             (make-registration ref options)))

          :register-pf
          (fn register-pf
            ([ref]
             (register-pf ref {}))
            ([ref options]
             (update (make-registration ref options) :on-change
               (fn [original]
                 (fn [_ event]
                   (original event))))))})

       {; (name: string | string[], options) => void
        :unregister
        (let [unregister (.-unregister form)]
          (fn unregister'
            ([name-or-names]
             (unregister' name-or-names {}))
            ([name-or-names options]
             (unregister (clj->js name-or-names)
               (cc-map options)))))

        :form-state
        (let [form-state (.-formState form)]
          {:is-dirty (.-isDirty form-state)
           :dirty-fields (.-dirtyFields form-state)
           :touched-fields (.-touchedFields form-state)
           :is-submitted (.-isSubmitted form-state)
           :is-submit-successful (.-isSubmitSuccessful form-state)
           :is-submitting (.-isSubmitting form-state)
           :submit-count (.-submit-count form-state)
           :is-valid (.-isValid form-state)
           :is-validating (.-isValidating form-state)
           :errors
           (js->clj (.-errors form-state) :keywordize-keys true)})

        :watch
        (fn [handler & args]
          (let [handler
                (if (fn? handler)
                  (fn [values options]
                    (handler (js->clj values :keywordize-keys true)
                      (let [{:keys [name type]}
                            (js->clj options :keywordize-keys true)]
                        {:name (keyword name) :type (keyword type)})))
                  (clj->js handler))]
            (js->clj
              (->> args (map clj->js) (apply (.-watch form) handler))
              :keywordize-keys true)))

        :reset #(apply (.-reset form) (map clj->js %&))
        :set-error (.-setError form)
        :clear-errors (.-clearErrors form)
        :set-value
        (fn [field value options]
          ((.-setValue form)
           (name field) (clj->js value) (clj->js (or options {}))))

        :set-focus (.-setFocus form)
        :get-values (.-getValues form)
        :trigger (.-trigger form)
        :control (.-control form)}

       (let [wrap-callback (fn [f] #(f (js->clj %1 :keywordize-keys true) %2))

             handle-submit
             (fn [& callbacks]
               (apply (.-handleSubmit form) (map wrap-callback callbacks)))]
         {:handle-submit handle-submit

          :submit
          (handle-submit
            (fn [data e]
              (.preventDefault e)
              (m/close! data)))})))))

(defn modal-submit
  ([form]
   (modal-submit form identity))
  ([{:keys [handle-submit]} transform & args]
   (handle-submit
     (fn [ok e]
       (.preventDefault e)
       (m/close! (apply transform ok args))))))

(defn c-controller [props]
  [:> Controller
   (-> props
     (u/update-when :rules clj->js)
     (u/update-when :name name)
     (update :render
       (fn [render]
         (fn [^js in]
           (uix/as-element
             (render
               (js->clj
                 {:field
                  (let [^js field (.-field in)]
                    {:on-change (.-onChange field)
                     :on-blur (.-onBlur field)
                     :value (.-value field)
                     :ref (.-ref field)})

                  :field-state
                  (let [^js field-state (.-fieldState in)]
                    {:invalid (.-invalid field-state)
                     :is-touched (.-isTouched field-state)
                     :is-dirty (.-isDirty field-state)
                     :error (.-error field-state)})

                  :form-state
                  (let [^js form-state (.-formState in)]
                    {:is-submit-successful (.-isSubmitSuccessful form-state)
                     :is-dirty (.-isDirty form-state)
                     :is-submitted (.-isSubmitted form-state)
                     :dirty-fields (.-dirtyFields form-state)
                     :touched-fields (.-touchedFields form-state)
                     :is-submitting (.-isSubmitting form-state)
                     :submit-count (.-submitCount form-state)
                     :is-valid (.-isValid form-state)
                     :is-validating (.-isValidating form-state)})})))))))])

(defn c-form-group
  ([name form body]
   (c-form-group {} name form body))
  ([props name form body]
   [pf/c-form-group
    (-> props
      (update :helper-text #(or % w/nbsp))
      (merge
        (when-let [{:keys [type message]} (get-in form [:form-state :errors name])]
          {:helper-text-invalid
           (if-not (str/blank? message) message type)
           :validated "error"})))
    body]))
